import {
  ComplaintMessagesDocument,
  type ComplaintMessagesQuery,
  CreateComplaintMessageDocument,
  type CreateComplaintMessageMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DEFAULT_PAGINATED_CONNECTION } from 'modules/complaints/hooks/utils'

interface UseComplaintMessagesOptions {
  complaintId: string
  enabled?: boolean
  first?: number
  sort?: { field: string; order: string }
  onlyFirst?: boolean
}

export const useComplaintMessages = ({
  complaintId,
  enabled = true,
  first = 20,
  sort = { field: 'created_at', order: 'DESC' },
  onlyFirst = false,
}: UseComplaintMessagesOptions) => {
  const {
    nodes: messages,
    controls,
    loading,
    error,
  } = usePaginatedQuery(ComplaintMessagesDocument, {
    variables: {
      complaintIds: [complaintId],
      sort: onlyFirst ? { field: 'created_at', order: 'ASC' } : sort,
      first: onlyFirst ? 1 : first,
    },
    skip: !enabled || !complaintId,
    extractConnection: (data: ComplaintMessagesQuery) =>
      data.complaints.messagesConnection ?? DEFAULT_PAGINATED_CONNECTION,
  })

  const [executeMutation, { loading: isCreating }] = useMutation(CreateComplaintMessageDocument, {
    extractorFn: (complaintMessage: CreateComplaintMessageMutation) =>
      complaintMessage.complaintsMutations.createComplaintsMessage,
    onCompleted: () => {
      app.setFlash({
        type: 'correct' as const,
        message: i18n.t('complaints.activity.message_created'),
      })
    },
    onError: (err: Error) => {
      app.setFlash({
        type: 'wrong' as const,
        message: err.message || i18n.t('common.error'),
      })
    },
  })

  const createMessage = async (input: { complaintId: string; text: string; files?: File[] }) => {
    const { files = [], ...graphqlInput } = input

    return await executeMutation({
      variables: {
        ...graphqlInput,
        isCompanyMessage: true,
        files: files.length > 0 ? files : undefined,
        blobIds: [],
      },
      refetchQueries: [ComplaintMessagesDocument],
    })
  }

  return {
    messages,
    createMessage,
    isCreating,
    controls,
    loading,
    error,
  }
}
